import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout"
import Spinner from "@rio-cloud/rio-uikit/Spinner"
import { useEffect, useMemo } from "react"
import { FormattedMessage } from "react-intl"
import { useLocation, useNavigate } from "react-router"
import { useGetInboundTransportOrdersQuery } from "@/client/transportorder-hub/transportOrderHubApi"
import { config } from "@/configuration/app/app-config"
import {
    BulkFreight,
    CargoFreight,
    Freight,
    FreightForwarder,
    Shipment,
    ShippingParty,
    TransportOrderType
} from "@/model/transportorder/transportOrder"
import { InboundTransportOrderTable } from "./InboundTransportOrderTable"
import { InboundTransportOrderToolbar } from "./InboundTransportOrderToolbar"
import {
    extractInboundUrlState,
    InboundSortField,
    mapInboundDateRangeToFetchParams,
    stringifyInboundUrlState,
} from "./inboundTransportOrderUrlState"

const sortValue = (shipment: Shipment, sortBy: InboundSortField): string | undefined => {
    switch (sortBy) {
    case InboundSortField.DOCUMENT_DATE:
        return shipment.createdDate
    case InboundSortField.REQUESTED_PICKUP_DATE_AT_SHIP_FROM:
        return shipment.startOfTransportChain.pickupRequestedAt.time_from
    case InboundSortField.ESTIMATED_ARRIVAL_DATE_OF_CURRENT_LEG:
        return shipment.currentTransportLeg.arrivalEstimatedAt?.time_from
    case InboundSortField.CALCULATED_ARRIVAL_DATE_AT_SHIP_TO:
        return shipment.endOfTransportChain.deliveryEstimatedAt?.time_to
    }
}

const sortTimestamp = (shipment: Shipment, sortBy: InboundSortField): number | undefined => {
    const value = sortValue(shipment, sortBy)
    if (!value) {
        return undefined
    }
    const timestamp = new Date(value).getTime()
    return Number.isFinite(timestamp) ? timestamp : undefined
}

const orderType = (shipment: Shipment): TransportOrderType => {
    switch (shipment.freight.type) {
    case "CARGO_EMPTIES":
        return TransportOrderType.EMPTIES
    case "BULK_DRY":
    case "BULK_LIQUID":
    case "CARGO_MATERIAL":
        return TransportOrderType.MATERIAL
    }
}

const partyKeywords = (party: ShippingParty): string[] => [
    party.name ?? "",
    party.externalIdentifiers.duns ?? "",
    party.externalIdentifiers.supplierCode ?? "",
    party.address?.street ?? "",
    party.address?.postal_code ?? "",
    party.address?.city ?? "",
    party.address?.country ?? "",
]

const forwarderKeywords = (forwarder: FreightForwarder): string[] => [
    forwarder.name,
    forwarder.supplierCode,
    forwarder.duns,
]

const freightKeywords = (freight: Freight): string[] => {
    switch (freight.type) {
    case "BULK_DRY":
    case "BULK_LIQUID":
        return [
            (freight as BulkFreight).article_id ?? ""
        ]
    case "CARGO_EMPTIES":
    case "CARGO_MATERIAL":
        return (freight as CargoFreight).handling_units.map(it => it.packaging_material_id ?? "")
    }
    
}

const searchKeywords = (shipment: Shipment): string[] => [
    shipment.external_identification ?? "",
    ...partyKeywords(shipment.startOfTransportChain.consignor),
    ...partyKeywords(shipment.endOfTransportChain.consignee),
    ...forwarderKeywords(shipment.freightForwarder),
    ...freightKeywords(shipment.freight)
].filter(Boolean)

export const InboundTransportOrders = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const urlState = useMemo(() => extractInboundUrlState(location.search), [location.search])
    const normalizedSearch = useMemo(() => stringifyInboundUrlState(urlState), [urlState])

    useEffect(() => {
        if (location.search.replace(/^\?/, "") !== normalizedSearch) {
            navigate({ search: normalizedSearch }, { replace: true })
        }
    }, [location.search, navigate, normalizedSearch])

    const updateUrlState = (change: Partial<typeof urlState>) => {
        navigate({ search: stringifyInboundUrlState({ ...urlState, ...change }) })
    }

    const { data, isFetching, isError } = useGetInboundTransportOrdersQuery(
        mapInboundDateRangeToFetchParams(urlState),
        {
            pollingInterval: config.transportOrderApi.pollIntervalMillis,
            refetchOnReconnect: true,
            skipPollingIfUnfocused: true,
        },
    )
    const transportOrders = useMemo(() => data ?? [], [data])

    const visibleRows = useMemo(() => {
        const normalizedSearchPhrase = urlState.searchPhrase.trim().toLocaleLowerCase()
        return transportOrders
            .filter(transportOrder =>
                urlState.orderType === undefined || orderType(transportOrder) === urlState.orderType
            )
            .filter(transportOrder => normalizedSearchPhrase === "" || searchKeywords(transportOrder).some(keyword =>
                keyword.toLocaleLowerCase().includes(normalizedSearchPhrase)
            ))
            .sort((left, right) => {
                const leftTimestamp = sortTimestamp(left, urlState.sortBy)
                const rightTimestamp = sortTimestamp(right, urlState.sortBy)
                if (leftTimestamp === undefined) {
                    return rightTimestamp === undefined ? 0 : 1
                }
                if (rightTimestamp === undefined) {
                    return -1
                }
                return urlState.sortDirection === "asc"
                    ? leftTimestamp - rightTimestamp
                    : rightTimestamp - leftTimestamp
            })
    }, [transportOrders, urlState.orderType, urlState.searchPhrase, urlState.sortBy, urlState.sortDirection])

    return (
        <ApplicationLayout.Body>
            <InboundTransportOrderToolbar
                dateFilterType={urlState.dateFilterType}
                dateFrom={urlState.dateFrom}
                dateTo={urlState.dateTo}
                orderType={urlState.orderType}
                searchPhrase={urlState.searchPhrase}
                visibleRows={visibleRows}
                onChange={updateUrlState}
            />
            {isFetching && transportOrders.length === 0
                ? <Spinner isFullSized/>
                : isError
                    ? <div className="alert alert-danger margin-15" role="alert">
                        <FormattedMessage id="inboundTransportOrders.error"/>
                    </div>
                    : <InboundTransportOrderTable
                        rows={visibleRows}
                        sortBy={urlState.sortBy}
                        sortDirection={urlState.sortDirection}
                        onSortChange={(sortBy, sortDirection) => updateUrlState({ sortBy, sortDirection })}
                    />
            }
        </ApplicationLayout.Body>
    )
}
